import openstreetUrls from "api/urls/openstreet";
import axios from "axios";

import { findFirstRelevantNominatimResult } from "../lib/nominatimPlaceFilter";
import { Coordinates, MapAddressSuggestion } from "../model/types";

const NOMINATIM_SUGGEST_LIMIT = 10;
const NOMINATIM_GEOCODE_LIMIT = 10;

const searchAddress = async (query: string, signal?: AbortSignal, limit?: number) => {
  if (!query.trim()) return [];
  const params = new URLSearchParams({
    addressdetails: "1",
    format: "jsonv2",
    layer: "address",
    limit: String(limit ?? 1),
    q: query.trim(),
  });
  try {
    const response = await axios.get(openstreetUrls.search(params), { signal });
    const data = response.data;
    return data;
  } catch {
    return [];
  }
};

export const searchAddressSuggestions = async (
  query: string,
  signal?: AbortSignal
): Promise<MapAddressSuggestion[]> => {
  try {
    const data = await searchAddress(query, signal, NOMINATIM_SUGGEST_LIMIT);
    if (!Array.isArray(data)) return [];
    return data.map((item: Record<string, unknown>, index: number): MapAddressSuggestion => {
      const displayName = String(item.display_name ?? "");
      const parts = displayName
        .split(", ")
        .map((s) => s.trim())
        .filter(Boolean);
      const primary = parts[0] || displayName;
      const secondary = parts.slice(1).join(", ");
      const placeId = item.place_id;
      const id =
        placeId != null ? String(placeId) : `${String(item.osm_type ?? "")}-${String(item.osm_id ?? "")}-${index}`;
      return {
        id,
        lat: Number(item.lat),
        lng: Number(item.lon),
        primary,
        secondary,
      };
    });
  } catch {
    return [];
  }
};

export const reverseGeocode = async (coords: Coordinates) => {
  const params = new URLSearchParams({
    format: "jsonv2",
    lat: String(coords.lat),
    lon: String(coords.lng),
  });
  try {
    const response = await axios.get(openstreetUrls.reverse(params));
    const data = response.data;
    return data?.address;
  } catch {
    return;
  }
};

export type GeocodedAddressResult = Coordinates & {
  country?: string;
};

const geocodeFirstMatch = async (queries: string[], signal?: AbortSignal): Promise<GeocodedAddressResult | null> => {
  for (const query of queries) {
    if (signal?.aborted) return null;

    const data = await searchAddress(query, signal, NOMINATIM_GEOCODE_LIMIT);
    const item = findFirstRelevantNominatimResult(data);
    if (!item) continue;

    const coords = {
      lat: Number(item.lat),
      lng: Number(item.lon),
    };
    const address = item.address as Record<string, string | undefined> | undefined;
    let country = address?.country;

    if (!country) {
      const reversed = await reverseGeocode(coords);
      country = reversed?.country;
    }

    return {
      ...coords,
      ...(country ? { country } : {}),
    };
  }

  return null;
};

export const geocodeAddressWithFallback = async (
  queries: string[],
  signal?: AbortSignal
): Promise<GeocodedAddressResult | null> => {
  try {
    return await geocodeFirstMatch(queries, signal);
  } catch {
    return null;
  }
};
